export type StationRole = "baby" | "parent";

export interface SessionDescription {
  type: "offer" | "answer";
  sdp: string;
}

export interface IceCandidate {
  candidate?: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}

export interface WebPushSubscription {
  endpoint: string;
  expirationTime: number | null;
  keys: { p256dh: string; auth: string };
}

export interface AlertSettingsMessage {
  type: "alert-settings";
  deviceId: string;
  thresholdDb: number;
  pushEnabled: boolean;
}

export interface JoinMessage {
  type: "join";
  role: StationRole;
  clientId: string;
  deviceId: string;
}

export interface OfferMessage {
  type: "offer";
  peerId: string;
  sdp: SessionDescription;
}

export interface AnswerMessage {
  type: "answer";
  peerId: string;
  sdp: SessionDescription;
}

export interface IceCandidateMessage {
  type: "ice-candidate";
  peerId: string;
  candidate: IceCandidate;
}

export type ClientMessage =
  | JoinMessage
  | OfferMessage
  | AnswerMessage
  | IceCandidateMessage
  | AlertSettingsMessage
  | { type: "parent-visibility"; visible: boolean }
  | { type: "noise-alert"; db: number };

export type ServerMessage =
  | { type: "joined"; role: StationRole }
  | { type: "waiting" }
  | { type: "pair-ready"; peerId: string }
  | OfferMessage
  | AnswerMessage
  | IceCandidateMessage
  | AlertSettingsMessage
  | { type: "noise-detected"; at: number }
  | { type: "partner-left"; peerId: string }
  | { type: "role-unavailable"; role: StationRole }
  | { type: "error"; message: string };

export interface LevelMessage {
  type: "level";
  db: number;
}

export function isStationRole(value: unknown): value is StationRole {
  return value === "baby" || value === "parent";
}

export function isDeviceId(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{8,80}$/.test(value);
}

export function parseClientMessage(raw: string): ClientMessage | null {
  if (raw.length > 65_536) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || typeof value.type !== "string") return null;
    if (value.type === "join" && isStationRole(value.role) && isDeviceId(value.clientId) && isDeviceId(value.deviceId)) {
      return { type: "join", role: value.role, clientId: value.clientId, deviceId: value.deviceId };
    }
    if ((value.type === "offer" || value.type === "answer") && isDeviceId(value.peerId) && isSessionDescription(value.sdp)) {
      return { type: value.type, peerId: value.peerId, sdp: value.sdp };
    }
    if (value.type === "ice-candidate" && isDeviceId(value.peerId) && isIceCandidate(value.candidate)) {
      return { type: "ice-candidate", peerId: value.peerId, candidate: value.candidate };
    }
    if (value.type === "alert-settings" && isAlertSettings(value)) {
      return {
        type: "alert-settings",
        deviceId: value.deviceId,
        thresholdDb: value.thresholdDb,
        pushEnabled: value.pushEnabled,
      };
    }
    if (value.type === "parent-visibility" && typeof value.visible === "boolean") {
      return { type: "parent-visibility", visible: value.visible };
    }
    if (value.type === "noise-alert" && isDb(value.db)) return { type: "noise-alert", db: value.db };
  } catch {
    return null;
  }
  return null;
}

export function parseServerMessage(raw: string): ServerMessage | null {
  if (raw.length > 65_536) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || typeof value.type !== "string") return null;
    if (value.type === "joined" && isStationRole(value.role)) return { type: "joined", role: value.role };
    if (value.type === "waiting") return { type: "waiting" };
    if (value.type === "pair-ready" && isDeviceId(value.peerId)) return { type: "pair-ready", peerId: value.peerId };
    if (value.type === "partner-left" && isDeviceId(value.peerId)) return { type: "partner-left", peerId: value.peerId };
    if (value.type === "role-unavailable" && isStationRole(value.role)) return { type: "role-unavailable", role: value.role };
    if (value.type === "error" && typeof value.message === "string") return { type: "error", message: value.message };
    if ((value.type === "offer" || value.type === "answer") && isDeviceId(value.peerId) && isSessionDescription(value.sdp)) {
      return { type: value.type, peerId: value.peerId, sdp: value.sdp };
    }
    if (value.type === "ice-candidate" && isDeviceId(value.peerId) && isIceCandidate(value.candidate)) {
      return { type: "ice-candidate", peerId: value.peerId, candidate: value.candidate };
    }
    if (value.type === "alert-settings" && isAlertSettings(value)) return value as unknown as AlertSettingsMessage;
    if (value.type === "noise-detected" && typeof value.at === "number" && Number.isFinite(value.at)) {
      return { type: "noise-detected", at: value.at };
    }
  } catch {
    return null;
  }
  return null;
}

export function parseLevelMessage(raw: string): LevelMessage | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (isRecord(value) && value.type === "level" && typeof value.db === "number" && Number.isFinite(value.db)) {
      return { type: "level", db: Math.max(-100, Math.min(0, value.db)) };
    }
  } catch {
    // Ignore malformed peer data.
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isDb(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -100 && value <= 0;
}

function isSessionDescription(value: unknown): value is SessionDescription {
  return isRecord(value) && (value.type === "offer" || value.type === "answer") && typeof value.sdp === "string";
}

function isIceCandidate(value: unknown): value is IceCandidate {
  return isRecord(value) && (value.candidate === undefined || typeof value.candidate === "string");
}

function isAlertSettings(value: Record<string, unknown>): value is Record<string, unknown> & AlertSettingsMessage {
  return (
    isDeviceId(value.deviceId) &&
    typeof value.thresholdDb === "number" &&
    Number.isFinite(value.thresholdDb) &&
    value.thresholdDb >= -60 &&
    value.thresholdDb <= -10 &&
    typeof value.pushEnabled === "boolean"
  );
}

export function isWebPushSubscription(value: unknown): value is WebPushSubscription {
  if (!isRecord(value) || typeof value.endpoint !== "string" || !value.endpoint.startsWith("https://")) return false;
  if (value.endpoint.length > 2_048 || (value.expirationTime !== null && typeof value.expirationTime !== "number")) return false;
  return (
    isRecord(value.keys) &&
    typeof value.keys.p256dh === "string" &&
    value.keys.p256dh.length <= 256 &&
    typeof value.keys.auth === "string" &&
    value.keys.auth.length <= 256
  );
}
